import React, { useEffect, useState } from "react";

// ESRI Components
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Query from "@arcgis/core/rest/support/Query";
import * as query from "@arcgis/core/rest/query.js";
import * as projection from "@arcgis/core/geometry/projection";
import type MapView from "@arcgis/core/views/MapView";
import type toast from "react-hot-toast";
import type Geometry from "@arcgis/core/geometry/Geometry.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol.js";
import Color from "@arcgis/core/Color.js";



import StreetView from "./StreetView";

import { Icon, Menu } from "semantic-ui-react";

interface Props {
  service: string;
  google_street_view_api_key: string;
  view: MapView;
  render_explorer: boolean;
  toast: typeof toast;
  localConfig: any;
  setLocalConfig: any;
}

interface Coordinates {
  x: number;
  y: number;
}

interface Style {
  border: string;
  margin: number;
  position: string;
  fontSize: string;
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const ContextMenuWidget = ({
  service,
  google_street_view_api_key,
  view,
  render_explorer,
  toast,
  localConfig,
  setLocalConfig,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [address, setAddress] = useState<string>("No Address Found");
  const [graphic, setGraphic] = useState<Graphic | null>(null)
  const [style, setStyle] = useState<Style>()
  const [queryObj] = useState<Query>(new Query());
  const [layer] = useState<GraphicsLayer>(
    new GraphicsLayer({
      id: "ContextMenuLayer",
    })
  );

  useEffect(() => {
    // set up the query
    queryObj.returnGeometry = false;
    queryObj.outFields = ["FullSitus"];
    queryObj.outSpatialReference = new SpatialReference({
      wkid: 4326,
    });


    view.map.layers.add(layer);

    // Listens for hold events (touch enabled devices)
    view.on("hold", (event) => {
      if (event.pointerType === "touch") {
        buildContent(event);
      } else {
        // If the state is visible then turn it off
        if (visible) {
          setVisible(false);
        }
      }
    });

    // Listens for right mouse click button
    view.on("click", (event) => {
      // console.log({ event });
      if (event.button === 2) {
        buildContent(event);
      } else if (visible) {
        setVisible(false);
        removeGraphic();
      }
    });
  }, []);

  useEffect(() => {
    if (graphic) {
      layer.graphics.add(graphic);
    }
  }, [layer, graphic])

  const buildContent = (event) => {
    console.log({ event });
    removeGraphic();

    projection.load().then(() => {
      var coordResults = projection.project(
        [event.mapPoint],
        queryObj.outSpatialReference
      ) as any[]

      // update the query geometry
      queryObj.geometry = event.mapPoint;

      // Execute the queryTask
      query.executeQueryJSON(service, queryObj).then((results) => {
        // Set the address
        var address = "No Address Found";
        if (results.features.length === 1) {
          address = results.features[0].attributes.FullSitus;
        }

        console.log({address, coordResults})

        if (coordResults.length === 1) {
          setCoordinates({
            x: coordResults[0].x,
            y: coordResults[0].y
          })
        }

        // add the graphic to the view
        const newGraphic = new Graphic({
          geometry: event.mapPoint,
          symbol: new SimpleMarkerSymbol({
            size: 14,
            color: [68, 68, 68, 1],
            outline: new SimpleLineSymbol({
              width: 2,
              color: [235, 235, 235, 1],
            })
          })
        });

        // Add the layer to the map
        view.map.reorder(layer, 100);
        setGraphic(newGraphic)

        // Initialze the watch handler to remove the contexte menu when the extent changes
        const watch_handle = view.watch("extent", () => {
          // Remove the extent handler
          watch_handle.remove();
          removeGraphic();
          setVisible(false)
        });

        // Set the default options to null
        var left = null;
        var right = null;
        var top = null;
        var bottom = null;

        // Set the Left or Right
        if (event.x + 210 >= view.size[0]) {
          right = view.size[0] - event.x;
        } else {
          left = event.x;
        }

        // Set the Top or Bottom
        if (event.y + 210 >= view.size[1]) {
          bottom = view.size[1] - event.y - 40; // Header is 40px
        } else {
          top = event.y;
        }

        console.error({
          size: view.size,
          x: event.x,
          y: event.y,
          top,
          right,
          bottom,
          left,
        });

        setAddress(address)
        setStyle({
          border: "none",
          margin: 0,
          position: "absolute",
          fontSize: "10pt",
          left: left,
          right: right,
          top: top,
          bottom: bottom,
        })
        setVisible(true)
      });
    });
  };

  const removeGraphic = () => {
    layer.graphics.removeAll();
  };

  const checkStreetView = async () => {
    console.log("Street view");
    var result = await StreetView({
      key: google_street_view_api_key,
      x: coordinates.x,
      y: coordinates.y,
    });
    console.log({ result });
    if (result) {
      window.open(result);
    } else {
      toast(
        <p>
          <Icon name="remove" color="red" />
          No Google Street View Panoramics found
        </p>,
        {}
      );
    }
  };

  if (!visible) {
    return
  }

  return (
    <Menu vertical collapsing="true" style={style}>
      <Menu.Item
        style={{
          fontWeight: "700",
          background: "#444",
          color: "#fff",
        }}
      >
        {address}
      </Menu.Item>
      {!render_explorer && (
        <Menu.Item
          target="_blank"
          href={`https://www.dmcwebgis.com/apps/pictometry/?x=${coordinates.x}&y=${coordinates.y}`}
        >
          <Icon name="external" />
          Pictometry
        </Menu.Item>
      )}
      {render_explorer && (
        <Menu.Item
          target="_blank"
          href={`http://explorer.pictometry.com/index.php?lat=${coordinates.y}&lon=${coordinates.x}`}
        >
          <Icon name="external" />
          Pictometry Explorer
        </Menu.Item>
      )}
      <Menu.Item
        target="_blank"
        href={`http://maps.google.com/maps?q=${coordinates.y},${coordinates.x}`}
      >
        <Icon name="google" />
        Google Maps
      </Menu.Item>
      <Menu.Item onClick={() => checkStreetView()}>
        <Icon name="google" />
        Google Street View
      </Menu.Item>
    </Menu>
  );
};

export default ContextMenuWidget;
